import { useState } from 'react'
import { useSIPContext } from '../../../state-management/contexts/SIPContext'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { AddUPIPage } from '../AddUPIPage'
import { useMutation } from '@tanstack/react-query'
import {
  InvestPaymentMethodType,
  InvestPaymentMethodTypes,
  PaymentPageType,
  UPISetupPayload,
  UPISetupResponse
} from '../../../types/mf-execution.types'
import { setupUPI } from '../../../repository/mfExecution'
import { useLoading } from '../../../state-management/contexts/LoadingContext'
import { useMetaDataContext } from '../../../state-management/contexts/MetaDataContext'
import { useNavigate } from 'react-router-dom'

export const SIPAddUpiPage = () => {
  const [isSetupError, setIsSetupError] = useState(false)
  const navigate = useNavigate()
  const { isin, data: metaData } = useMetaDataContext()
  const fundName = metaData?.fundDetails?.fundName ?? ''
  const trackingFund = isin && fundName ? { fundIsin: isin, fundName } : undefined
  const { showLoading, hideLoading } = useLoading()
  const { amount, paymentMethods, handleRefetchPaymentMethods, handlePaymentMethodChange } = useSIPContext()

  const handleSetupUpiSuccess = (data: UPISetupResponse, payload: UPISetupPayload) => {
    const displayAccNumber = paymentMethods?.find(
      (method) => method?.accountNumber === payload?.accountNumber
    )?.displayAccNumber
    handleRefetchPaymentMethods()
    handlePaymentMethodChange({
      paymentMethodId: data?.paymentMethodId,
      type: InvestPaymentMethodTypes.UPI,
      displayAccNumber: displayAccNumber,
      upiId: payload?.upiId
    } as InvestPaymentMethodType)
  }

  const { mutate: setupUpiMutate } = useMutation({
    mutationFn: (payload: UPISetupPayload) => setupUPI(payload),
    onSuccess: (data: UPISetupResponse, variables: UPISetupPayload) => {
      if (data?.status === 'INVALID') {
        hideLoading()
        setIsSetupError(true)
        return
      }
      handleSetupUpiSuccess(data, variables)
      setTimeout(() => {
        hideLoading()
        navigate(`/mutual-fund/${isin}/payment/${PaymentPageType.SIP}?isConfirm=true`)
      }, 250)
    },
    onError: () => {
      setIsSetupError(true)
      hideLoading()
    }
  })

  const handleSetupUpi = (payload: UPISetupPayload) => {
    showLoading()
    setupUpiMutate(payload)
  }

  return (
    <AddUPIPage
      trackingFund={trackingFund}
      bankAccounts={
        paymentMethods
          ?.filter((method) => method?.type === InvestPaymentMethodTypes.NET_BANKING)
          ?.map((account) => {
            return {
              logoUrl: account?.logoUrl ?? '/images/mutual_fund/categories/banks_grayscale.svg',
              displayAccNumber: account?.displayAccNumber ?? '',
              paymentMethodId: account?.paymentMethodId ?? '',
              accountNumber: account?.accountNumber ?? '',
              ifscCode: account?.ifscCode ?? ''
            }
          }) ?? []
      }
      cta={{ text: `Pay ${roundOffAndFormatNumberWithCurrency(amount)}`, onClick: handleSetupUpi }}
      isSetupError={isSetupError}
    />
  )
}
